import React, { useState, useEffect, useRef } from "react";

import { Col, FormGroup, Input, Button, Label } from "reactstrap";
import { LinkRowFields, handleLink } from "../LinkRowFields";

/** A sub-link under an Array:links row; the NewDesign navbar renders these as a dropdown (SCRUM-2171). */
export type ChildLink = { name?: string; link?: string };

/** One Array:links row; `index` is this editor's own row key, starting at 1. */
export type LinkRow = { name?: string; link?: string; index: number; children?: ChildLink[] };

/** The content variable whose `value` holds the links. */
type LinksVariable = { variable?: string; value?: unknown };

type LinksHandler = (links: LinkRow[], variableName?: string) => void;

export type ArrayLinksEditorProps = {
  settingVar: LinksVariable;
  linksHandler: LinksHandler;
  linksRequired?: boolean;
};

/**
 * SCRUM-2195: the one Array:links editor, for the update screens and (through
 * TemplateSelector/ArrayLinks) the create wizard. The wizard's differences are
 * props: its start rows arrive as `variableName.value`, `isRequired` is true in
 * both of its columns, its rows are uncontrolled and it has no sub-links.
 */
export type ContentArrayLinksProps = {
  variableName: LinksVariable;
  /** The last row cannot be deleted. */
  isRequired?: boolean;
  /** Rows show their stored values (update screens); the wizard's are uncontrolled. */
  controlled?: boolean;
  /** Each row can carry sub-links (SCRUM-2171); update screens only. */
  subLinks?: boolean;
  handleLinkArray: LinksHandler;
  add: LinksHandler;
  delete: LinksHandler;
};

// Host chrome for the update screens' Array:links rows (SCRUM-643): legacy
// label + the delegated editor, as one module-level component so the hosts'
// override maps stay static (a per-render closure here would remount the
// editor — and reset its local rows — on every override-map rebuild). The
// row handler and the delete-guard travel via VariableField's fieldProps
// (`linksHandler` / `linksRequired`); Required leaves linksRequired at its
// default (true, delete disabled at one row), Optional passes false (pinned
// legacy deviation).
export const ArrayLinksEditor = ({ settingVar, linksHandler, linksRequired = true }: ArrayLinksEditorProps) => (
  <div>
    <Label className="form-control-label" htmlFor="example-text-input" md="2">
      {settingVar.variable}
    </Label>
    <ContentArrayLinks
      variableName={settingVar}
      isRequired={linksRequired}
      handleLinkArray={linksHandler}
      add={linksHandler}
      delete={linksHandler}
    />
  </div>
);
ArrayLinksEditor.ownsChrome = true;

const ColoredLine = ({ color }: { color: string }) => (
    <hr
        style={{
            color: color,
            backgroundColor: color,
            height: 5
        }}
    />
);

function ContentArrayLinks(props: ContentArrayLinksProps) {
    // Ensure value is an array - handle cases where it might be a string or undefined
    const { controlled = true, subLinks = true } = props;
    const initialValue: { index?: number; name?: string; link?: string; children?: ChildLink[] }[] = Array.isArray(props.variableName.value)
        ? props.variableName.value
        : [{ name: "", link: "" }];

    const [Rows, setRows] = useState(initialValue.length)
    const isRequired = props.isRequired === true ? props.isRequired : false;
    // const [LinksArray, setLinksArray] = useState([])
    const [LinksArray, setLinksArray] = useState<LinkRow[]>(
        initialValue.map((linkObj, idx)=> {
            return {...linkObj, index:idx+1}
        })
    )
    // Rows whose sub-links are shown; a row stored with sub-links starts open.
    const [openRows, setOpenRows] = useState<Set<number>>(
        () => new Set(LinksArray.filter((row) => (row.children?.length ?? 0) > 0).map((row) => row.index))
    )

    // Sub-links are edited immutably: a new row object carries the new list, and
    // the whole row array goes out through the same handler as a name/link edit.
    const commitChildren = (row: LinkRow, children: ChildLink[]) => {
        const next = LinksArray.map((r) => (r.index === row.index ? { ...r, children } : r));
        setLinksArray(next);
        props.handleLinkArray(next, props.variableName.variable);
    }

    const toggleRow = (event: React.MouseEvent, index: number) => {
        event.preventDefault();
        setOpenRows((prev) => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    }

    // useEffect(() => {
    //     setLinksArray( props.variableName.value.map((linkObj, idx)=> {
    //         return {...linkObj, index:idx+1}
    //     })
        
    //     )
       

    // }, [LinksArray.length, Rows])

        // ADD Row
        const addRow = (event: React.MouseEvent) => {
            let linksState = LinksArray;
            event.preventDefault();
            setRows(Rows+1)
            setLinksArray([...LinksArray, {index: Rows+1, name:"", link:""}])
            linksState = [...linksState, {index: Rows+1, name:"", link:""}]
            props.add(linksState, props.variableName.variable)
        }
        //Delete Row
    
        const deleteRow = (event: React.MouseEvent) => {
            event.preventDefault();
            if (LinksArray.length === 0 || (isRequired && LinksArray.length <= 1)) return;
            // SCRUM-2195: slice, not splice. The splice changed the array held in
            // state and reported that same object, so a host that kept the first
            // report saw it shrink again on the next delete.
            const next = LinksArray.slice(0, -1)
            setRows(Rows-1)
            setLinksArray(next)
            props.delete(next, props.variableName.variable)
        }

        const onEdit = (e: React.ChangeEvent<HTMLInputElement>, link: LinkRow) =>
            props.handleLinkArray(handleLink(LinksArray, e, link), props.variableName.variable);
    return (<>
        {/* Buttons */}
        <div className="text-center">
            <Button
              className="btn-icon-only rounded-circle"
              color="facebook"
              href="#"
              disabled={true}
            >
              {LinksArray.length}
            </Button>
            <Button
              className="btn-slack btn-icon-only rounded-circle"
              color="default"
              onClick={addRow}
            >
              <i className="ni ni-fat-add" />
            </Button>
            <Button
              className="btn-icon-only rounded-circle"
              color="pinterest"
              onClick={deleteRow}
              disabled={isRequired && LinksArray.length === 1}
            >
              <i className="fas fa-trash" />
            </Button>
          </div>
          {/* END Buttons */}
  
          {/* <br></br> */}
          <ColoredLine color="red" />
          <div>
            {LinksArray.map((link) => {
              const children = link.children ?? [];
              const open = openRows.has(link.index);
              return (
                <React.Fragment key={link.index}>
                <LinkRowFields link={link} controlled={controlled} onEdit={onEdit} />
                {/* Sub-links (SCRUM-2171): the NewDesign navbar shows a row that
                    has them as a dropdown; a row without them stays a plain link. */}
                {subLinks && (
                <div className="ml-4 mb-3">
                  <Button
                    size="sm"
                    color="link"
                    aria-expanded={open}
                    onClick={(e) => toggleRow(e, link.index)}
                  >
                    {`Sub-links (${children.length})`}
                  </Button>
                  {open && (
                    <>
                      {children.map((child, ci) => (
                        <FormGroup className="row" key={ci}>
                          <Col md="5">
                            <Input
                              className="form-control-sm"
                              placeholder="sub-link name"
                              aria-label={`Sub-link ${ci + 1} name`}
                              type="text"
                              value={child.name ?? ""}
                              onChange={(e) =>
                                commitChildren(link, children.map((c, i) => (i === ci ? { ...c, name: e.target.value } : c)))
                              }
                            />
                          </Col>
                          <Col md="5">
                            <Input
                              className="form-control-sm"
                              placeholder="sub-link url"
                              aria-label={`Sub-link ${ci + 1} url`}
                              type="text"
                              value={child.link ?? ""}
                              onChange={(e) =>
                                commitChildren(link, children.map((c, i) => (i === ci ? { ...c, link: e.target.value } : c)))
                              }
                            />
                          </Col>
                          <Col md="2">
                            <Button
                              size="sm"
                              color="pinterest"
                              aria-label={`Remove sub-link ${ci + 1}`}
                              onClick={(e) => {
                                e.preventDefault();
                                commitChildren(link, children.filter((_, i) => i !== ci));
                              }}
                            >
                              <i className="fas fa-times" />
                            </Button>
                          </Col>
                        </FormGroup>
                      ))}
                      <Button
                        size="sm"
                        color="default"
                        onClick={(e) => {
                          e.preventDefault();
                          commitChildren(link, [...children, { name: "", link: "" }]);
                        }}
                      >
                        Add sub-link
                      </Button>
                    </>
                  )}
                </div>
                )}
                </React.Fragment>
              );
            })}
          </div>
          <ColoredLine color="red" />
  
        </>);
}

export default ContentArrayLinks
